-- | This module implements the signature widget, which displays the current
-- | signature and opens a modal to sign or edit it when clicked.
-- |
-- | Signature actions return requests to the app runtime. Local-only effects,
-- | such as copying an emailed link, stay in this widget.
module Widgets.Signature where

import Common

import API.Signature (BaseSigDefaults, SignOpts, SigVerification, SignResult, SignatureRequest)
import API.Types (DocumentID)
import Concur.Core (Widget)
import Concur.React.DOM as D
import Concur.React.Props as P
import Control.Alt ((<|>))
import Data.Array (catMaybes)
import Data.Time.Duration (Milliseconds(..))
import Effect.Aff (delay)
import Internal.Clipboard as CB
import Internal.ConcurUtil (loopInfinitely)
import Internal.Luxon (LDateTime)
import Internal.Luxon as DT
import Internal.Util (justIf)
import Widgets.Dropdown (dropdownButton)
import Widgets.Modal (ModalResult(..), errorWrapperModal, modalWidget)
import Widgets.Signature.Email (EmailAction(..), singleEmailWidget)
import Widgets.Signature.Form (SigFormSubmit(..), initSigFormState, signatureForm)
import Widgets.Signature.Pad (Signature, signatureImage)
import Widgets.Signature.Types (DocSigField, FormSigField, FormSignature, SignatureField, SignatureInfo, addSignature, emptySigInfo, normalizeDocSigField, normalizeFormSigField)


type SigWidgetState =
    { field :: SignatureField
    , info :: SignatureInfo
    , docId :: DocumentID
    , signature :: SigVariant
    }

data SigWidgetResult
    = SigWidgetChanged SigWidgetState
    | SigWidgetMutation SigWidgetState SigWidgetMutation

data SigWidgetMutation
    = UseCapturedSignature SignatureField FormSignature SignOpts
    | UseSavedSignature SignatureField
    | SendSignatureRequest SignatureRequest

data SigVariant
    = LocalSignature Signature LDateTime
    -- ^ A signature stored as a PNG in a binary blob.
    | RemoteSignature String LDateTime
    -- ^ A signature stored at a URL on the backend.
    | Emailed LDateTime String String
    -- ^ An email was sent to request a signature.
    | NotSigned
    -- ^ This signature widget hasn't been signed.

data SigWidgetAction
    = OpenSigForm
    | OpenEmailForm
    | SignWithSaved

data LoadDefaultsOp
    = AutofillOp
    | AuthoritativeOp

initSigWidget :: DocumentID -> SignatureField -> SignatureInfo -> SigWidgetState
initSigWidget docId field info = { docId, field, info, signature: NotSigned }

fromFormSigField :: DocumentID -> FormSigField -> SigWidgetState
fromFormSigField docId field =
    initSigWidget docId (normalizeFormSigField field) emptySigInfo

fromDocSigField :: DocumentID -> DocSigField -> SigWidgetState
fromDocSigField docId field =
    initSigWidget docId (normalizeDocSigField field) emptySigInfo

loadSignature :: SigWidgetState -> SignResult -> SigWidgetState
loadSignature state result = state
    { signature = RemoteSignature result.url result.created_at
    , info { first_name = result.contact.first_name
           , last_name = result.contact.last_name
           , email = result.contact.email
           }
    }

loadVerification :: SigWidgetState -> SigVerification -> SigWidgetState
loadVerification state result = state
    { signature = Emailed result.created_at comment result.link
    , info { email = result.data.email }
    }
  where
    comment = fromMaybe "" result.data.comment

loadDefaults :: forall r. LoadDefaultsOp -> SigWidgetState -> BaseSigDefaults r -> SigWidgetState
loadDefaults AutofillOp state@{ signature: NotSigned } def = state
    { info
        { first_name = fromMaybe state.info.first_name def.default_first_name
        , last_name = fromMaybe state.info.last_name def.default_last_name
        , email = fromMaybe state.info.email def.default_email
        }
    }
loadDefaults AuthoritativeOp state@{ signature: NotSigned } def = state
    { info
        { first_name = fromMaybe "" def.default_first_name
        , last_name = fromMaybe "" def.default_last_name
        , email = fromMaybe "" def.default_email
        }
    }
loadDefaults _ state _ = state

isWidgetSignedOrEmailed :: SigWidgetState -> Boolean
isWidgetSignedOrEmailed { signature: NotSigned } = false
isWidgetSignedOrEmailed { signature: _ } = true

isWidgetSigned :: SigWidgetState -> Boolean
isWidgetSigned { signature: NotSigned } = false
isWidgetSigned { signature: Emailed _ _ _ } = false
isWidgetSigned { signature: _ } = true

signedTime :: SigWidgetState -> Maybe LDateTime
signedTime { signature: RemoteSignature _ time } = Just time
signedTime { signature: LocalSignature _ time } = Just time
signedTime { signature: Emailed _ _ _ } = Nothing
signedTime { signature: NotSigned } = Nothing

-- | A widget which displays the signature form in a modal when clicked and
-- | then displays the current signature in an image tag.
signatureWidget :: SigWidgetState -> Widget HTML SigWidgetResult
signatureWidget state = renderSigWidget state >>= case _ of
    Left state' -> pure $ SigWidgetChanged state'
    Right act -> handleSWAction state act

handleSWAction :: SigWidgetState -> SigWidgetAction -> Widget HTML SigWidgetResult
handleSWAction state OpenSigForm = do
    -- TODO: Looad signature into the form.
    let initSig = addSignature state.info Nothing
    let sfState = initSigFormState state.docId state.field initSig
    res <- errorWrapperModal (renderSigWidget state) $ signatureForm sfState
    case res of
        ModalClosed -> pure $ SigWidgetChanged state
        ModalReturned (SubmitSignature sig opts) ->
            pure $ SigWidgetMutation state $ UseCapturedSignature state.field sig opts
        ModalReturned SubmitSavedSignature ->
            pure $ SigWidgetMutation state $ UseSavedSignature state.field

handleSWAction state OpenEmailForm = do
    let prevComment = case state.signature of
            Emailed _ com _ -> com
            _ -> ""
    res <- modalWidget (renderSigWidget state) $
        singleEmailWidget { email: state.info.email, comment: prevComment } state.docId state.field
    case res of
        ModalClosed -> pure $ SigWidgetChanged state
        -- If we're just saving the email, just set the email.
        ModalReturned (SaveEmail {email}) ->
            pure $ SigWidgetChanged state { info { email = email } }
        -- If the email was sent, set the emailed flag.
        ModalReturned (SaveAndSendEmail request) ->
            pure $ SigWidgetMutation (state { info { email = request.email } }) $ SendSignatureRequest request

handleSWAction state SignWithSaved =
    pure $ SigWidgetMutation state $ UseSavedSignature state.field

renderSigWidget :: SigWidgetState -> Widget HTML (Either SigWidgetState SigWidgetAction)
renderSigWidget state = D.div
    [ P.className "signature-widget" ]
    ([renderSigImage state
    , renderDropdownButton state
    , D.p [ P.className "signature-type" ] [ D.text state.field.label ]
    ] <> renderNameWidget state <>
    (if state.field.can.request then [ renderEmailWidget state ] else [])
    )

renderSigImage :: SigWidgetState -> Widget HTML (Either SigWidgetState SigWidgetAction)

renderSigImage { info: { email }, signature: Emailed time _ link } = D.div
    [ P.className "sign-here emailed" ]
    [ D.div [ P.className "emailed-notice" ]
        [ D.div' [ D.text (lookupTranslation "email-sent-to" <> " " <> email) ]
        , D.div' [ D.text (lookupTranslation "email-sent-at" <> " " <> DT.prettyPrint time) ]
        , D.div' [ sentLink "email-sent-link" ]
        ]
    ]
  where
    sentLinkBtn lbl = D.a
        [ P.onClick, P.href "javascript:void(0);"
        , P.className "signature-copy-link" ]
        [ D.text (lookupTranslation lbl) ]
    sentLink lbl = do
        _ <- sentLinkBtn lbl
        (affAction $ CB.writeText link) <|> (loopInfinitely $ sentLinkBtn lbl)
        ret <- (Left <$> sentLink "email-sent-link-copied") <|> do
           affAction $ delay $ Milliseconds 5000.0
           pure $ Right unit
        case ret of
            Left ret' -> pure ret'
            Right _ -> sentLink "email-sent-link"

renderSigImage { info: { first_name, last_name }, signature: LocalSignature sig _, field } = D.button
    ((if field.can.update then [ Right OpenSigForm <$ P.onClick ] else []) <>
      [ P.className "sign-here signed" ])
    [ signatureImage [ P.alt $ "Signature of " <> name ] sig ]
  where
    name = first_name <> " " <> last_name

renderSigImage { info: { first_name, last_name }, signature: RemoteSignature url _, field } = D.button
    ((if field.can.update then [ Right OpenSigForm <$ P.onClick ] else []) <>
      [ P.className "sign-here signed" ])
    [ D.img
        [ P.alt $ "Signature of " <> name
        , P.src url
        ]
    ]
  where
    name = first_name <> " " <> last_name

renderSigImage { signature: NotSigned, field: { can: { create: false } } } = D.button
    [ P.className "sign-here disabled" ]
    [ D.text $ lookupTranslation "no-sign-here" ]

renderSigImage { signature: NotSigned, field: { can: { create: true } } } = D.button
    [ Right OpenSigForm <$ P.onClick
    , P.className "sign-here" ]
    [ D.text $ lookupTranslation "sign-here" ]

renderNameWidget :: SigWidgetState -> Array (Widget HTML (Either SigWidgetState SigWidgetAction))
renderNameWidget state@{ info: { first_name, last_name } } = case signedTime state of
    Just time ->
        [ D.p [ P.className "signature-name" ] [ D.text name ]
        , D.p [ P.className "signature-time" ]
              [ D.text $ (lookupTranslation "signed-at") <> " " <> DT.prettyPrint time ]
        ]
    Nothing -> []
  where
    name = first_name <> " " <> last_name

renderEmailWidget :: SigWidgetState -> Widget HTML (Either SigWidgetState SigWidgetAction)
renderEmailWidget { info: { email: "" }, field: { can: { request: false } } } =
    D.div' []
renderEmailWidget { info: { email: "" } } =
    D.button
        [ Right OpenEmailForm <$ P.onClick
        , P.className "signature-add-email"
        ]
        [ D.text $ lookupTranslation "signature-add-email" ]
renderEmailWidget { info: { email }, signature } = D.p
    [ P.className "signature-email" ]
    [ D.text $ email <> " "
    , D.button
        [ Right OpenEmailForm <$ P.onClick, P.className "signature-add-email" ]
        [ D.text $ lookupTranslation sendText ]
    ]
  where
    sendText | alreadySent = "signature-resend-email"
    sendText = "signature-send-email"
    alreadySent = case signature of
        Emailed _ _ _ -> true
        _ -> false


renderDropdownButton :: SigWidgetState -> Widget HTML (Either SigWidgetState SigWidgetAction)
renderDropdownButton state = if emptyMenu
    then D.div' []
    else dropdownButton renderBtn menu
  where
    emptyMenu = length menu == 0
    menu = catMaybes
      [ justIf (state.field.can.create && not signed)
          { value: Right OpenSigForm
          , label: lookupTranslation "sign" }
      , justIf (state.field.can.useDefault && not signed)
          -- TODO: Sign without opening modal
          { value: Right SignWithSaved
          , label: lookupTranslation "sign-with-saved" }
      , justIf (state.field.can.request && not signedOrEmailed)
          -- TODO: Send emails without opening form
          { value: Right OpenEmailForm
          , label: lookupTranslation "email-to-sign" }
      , justIf (state.field.can.request) $ if state.info.email == ""
          then { value: Right OpenEmailForm
               , label: lookupTranslation "add-email" }
          else { value: Right OpenEmailForm
               , label: lookupTranslation "edit-email" }
      ]
    signed = isWidgetSigned state
    signedOrEmailed = isWidgetSignedOrEmailed state

    renderBtn = D.button
        [ P.className "dropdown-button", P.onClick]
        [ D.text "⠇" ]
